import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';

import {
  adminCreateGroupTutorRateCourseType,
  adminUpdateGroupTutorRateCourseType,
  IGroupTutorRateCourseType,
  TAdminCreateGroupTutorRateCourseType,
  TAdminUpdateGroupTutorRateCourseType,
} from '../../../../../../../store/V2/GroupTutorRateCourseTypesSlice';
import {
  getCurriculumsV2WithGrades,
  getCurriculumV2State,
  resetCurriculumsWithGradesList,
} from '../../../../../../../store/V2/CurriculumSlice';
import { useAppSelector } from '../../../../../../../hooks';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../components/Inputs';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../../components/V2/Buttons';

interface AddNewGroupTutorRateCourseTypeModalProps {
  open: boolean;
  onClose: () => void;
  groupTutorRateCourseType?: IGroupTutorRateCourseType;
  readOnly?: boolean;
  duplicate?: boolean;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TGradeItem = {
  curriculum: string;
  grade: string;
};

type TState = {
  name: string;
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  grades: TGradeItem[];
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  name: '',
  selectedCurriculum: null,
  selectedGrade: null,
  grades: [],
};

const AddNewGroupTutorRateCourseTypeModal: FunctionComponent<AddNewGroupTutorRateCourseTypeModalProps> = ({
  open,
  onClose,
  groupTutorRateCourseType,
  readOnly,
  duplicate,
}) => {
  const dispatch = useDispatch();

  // Store
  const curriculumState = useAppSelector(getCurriculumV2State);

  // State
  const [isLoading, setLoading] = useState(false);
  const [state, setState] = useState({ ...initialState });
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  const isSubmitDisabled = !state.name.trim().length || !state.grades.length;

  let mode: 'edit' | 'new' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Course Type';

  if (groupTutorRateCourseType) {
    mode = 'edit';
    modalTitle = 'Edit Course Type';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Course Type Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Course Type';
    }
  }

  useEffect(() => {
    dispatch(getCurriculumsV2WithGrades());

    if (mode !== 'new' && groupTutorRateCourseType) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (groupTutorRateCourseType.name) {
          newStateData.name = groupTutorRateCourseType.name;
        }

        if (groupTutorRateCourseType.grades) {
          newStateData.grades = groupTutorRateCourseType.grades;
        }

        return newStateData;
      });
    }

    return () => {
      dispatch(resetCurriculumsWithGradesList());
    };
  }, []);

  useEffect(() => {
    if (
      curriculumState.curriculumsWithGradesList &&
      curriculumState.curriculumsWithGradesList.length
    ) {
      setCurriculumOptions(
        curriculumState.curriculumsWithGradesList.map((curriculumItem) => ({
          label: curriculumItem.name,
          value: curriculumItem.name,
        }))
      );
    }
  }, [curriculumState.curriculumsWithGradesList]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedCurriculum: value,
      selectedGrade: null,
    });

    if (
      curriculumState.curriculumsWithGradesList &&
      curriculumState.curriculumsWithGradesList.length &&
      value
    ) {
      const selectedCurriculumData = curriculumState.curriculumsWithGradesList.find(
        (item) => item.name === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.grades &&
        selectedCurriculumData.grades.length
      ) {
        setGradeOptions(
          selectedCurriculumData.grades.map((item) => ({
            label: item.grade,
            value: item.grade,
          }))
        );
      }
    }
  };

  const handleAddGrade = () => {
    setState((prevState) => {
      const newStateData = { ...prevState, selectedGrade: null };

      if (
        state.selectedGrade &&
        state.selectedCurriculum &&
        !newStateData.grades.some(
          (item) =>
            state.selectedGrade &&
            item.grade === state.selectedGrade.value &&
            item.curriculum === state.selectedCurriculum?.value
        )
      ) {
        newStateData.grades = [
          ...prevState.grades,
          {
            curriculum: state.selectedCurriculum.value,
            grade: state.selectedGrade.value,
          },
        ];
      }

      return newStateData;
    });
  };

  const removeSelectedGrade = (grade: TGradeItem) => {
    setState((prevState) => {
      const newStateData = { ...prevState };

      newStateData.grades = [...prevState.grades].filter(
        (item) =>
          !(item.curriculum === grade.curriculum && item.grade === grade.grade)
      );

      return newStateData;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateGroupTutorRateCourseType = {
        name: state.name,
        grades: state.grades,
      };

      const res: any = await dispatch(
        adminCreateGroupTutorRateCourseType(apiData)
      );

      if (res.payload) {
        onClose();
      }
    } else if (mode === 'edit' && groupTutorRateCourseType) {
      const apiData: TAdminUpdateGroupTutorRateCourseType = {
        id: groupTutorRateCourseType._id,
        name: state.name,
        grades: state.grades,
      };

      const res: any = await dispatch(
        adminUpdateGroupTutorRateCourseType(apiData)
      );

      if (res.payload) {
        onClose();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px" alignItems="center">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Name *"
                  placeholder="Enter here..."
                  value={state.name}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight={600}>
                Grades *
              </Typography>
            </Grid>
            {mode !== 'view' ? (
              <>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={state.selectedCurriculum}
                      options={curriculumOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      loading={curriculumState.status === 'loading'}
                      getOptionKey={(option: TSelectOption) => option.value}
                      onChange={handleCurriculumChange}
                      isOptionEqualToValue={(option, value) =>
                        option.label === value.value
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Curriculum"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      disabled={!state.selectedCurriculum}
                      value={state.selectedGrade}
                      options={gradeOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      loading={curriculumState.status === 'loading'}
                      getOptionKey={(option: TSelectOption) => option.value}
                      isOptionEqualToValue={(option, value) =>
                        option.label === value.value
                      }
                      onChange={(event, value) =>
                        updateStateAttributes({ selectedGrade: value })
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Grade"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <DarkButton
                    variant="contained"
                    color="inherit"
                    sx={{ ...defaultButtonStyles }}
                    onClick={handleAddGrade}
                    disabled={!state.selectedGrade}
                  >
                    Add
                  </DarkButton>
                </Grid>
              </>
            ) : null}
            {state.grades.length ? (
              <Grid item xs={12}>
                <Stack gap="10px">
                  {state.grades.map((item, index) => {
                    return (
                      <Stack
                        key={`grade-item-${index}`}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography fontSize="14px" fontWeight={500}>
                          {item.curriculum} - {item.grade}
                        </Typography>
                        {mode !== 'view' ? (
                          <IconButton onClick={() => removeSelectedGrade(item)}>
                            <DeleteIcon sx={{ color: '#1E293B' }} />
                          </IconButton>
                        ) : null}
                      </Stack>
                    );
                  })}
                </Stack>
              </Grid>
            ) : null}
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AddNewGroupTutorRateCourseTypeModal;
